import { useEffect } from 'react';

import {
  DiscountV4ControllerApiFactory,
  DiscountV3ControllerApiFactory,
} from '@ce-openapi/moka-inventory-discount-service-ts-axios-client';
import { axios } from '@pukka/auth';
import { constants } from '@pukka/common';
import { queryClient } from '@pukka/tanstack-query';
import { InfiniteData, QueryClient, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { InventoryModel } from '../models';

interface GetDiscountsParams {
  readonly cursor?: string;
  readonly perPage?: number;
  readonly outletId?: number;
}

interface GetDiscountDetailParams {
  name: string;
  type: string;
  amount?: number;
}

interface MutationError {
  readonly code?: string;
  readonly errors?: string;
  readonly message?: string;
}

const { MOKA_GOAUTH_SERVICE } = constants;

// This is for MSW purpose since the generated OpenAPI client does not provide full path url
const DISCOUNTS_URL = `${MOKA_GOAUTH_SERVICE}/inventory/v4/discounts`;
const DISCOUNT_V3_URL = `${MOKA_GOAUTH_SERVICE}/inventory/v3/discounts`;
const DISCOUNT_V3_DETAIL_URL = `${MOKA_GOAUTH_SERVICE}/inventory/v3/discounts/detail`;
const MOKA_INVENTORY_API = `${MOKA_GOAUTH_SERVICE}/inventory`;

const discountApiV4 = DiscountV4ControllerApiFactory(
  undefined,
  MOKA_INVENTORY_API,
  axios as Parameters<typeof DiscountV4ControllerApiFactory>[2],
);

const discountApiV3 = DiscountV3ControllerApiFactory(
  undefined,
  MOKA_INVENTORY_API,
  axios as Parameters<typeof DiscountV4ControllerApiFactory>[2],
);

const handleFailedMutation = (error: AxiosError<MutationError>) => {
  if (!error.response) {
    throw error;
  }

  const {
    data: { code, errors, message },
  } = error.response;

  if (code === '0005-0001') {
    error.message = 'Discount does not found or already removed';
  } else if (code === '0005-0010') {
    error.message = 'Discount name already exists in your business, please use another name.';
  } else if (errors) {
    error.message = errors;
  } else if (message) {
    error.message = message;
  }

  throw error;
};

const listDiscounts = ({ cursor: currentCursor, perPage, outletId }: GetDiscountsParams) => {
  return discountApiV4
    .listDiscounts(undefined, undefined, outletId, currentCursor, perPage, false, undefined, undefined)
    .then((resp) => resp.data);
};

const getDiscountDetail = ({ name, type, amount }: GetDiscountDetailParams) =>
  discountApiV3.getDiscountDetail(name, type, undefined, undefined, amount).then((resp) => resp.data);

const createDiscount = (createDiscountRequest: InventoryModel.Discount.CreateRequest) =>
  discountApiV3
    .createDiscount(createDiscountRequest)
    .then((resp) => resp.data)
    .catch(handleFailedMutation);

const deleteDiscount = (deleteDiscountRequest: InventoryModel.Discount.DeleteRequest) =>
  discountApiV3
    .deleteDiscount(deleteDiscountRequest)
    .then((resp) => resp.data)
    .catch(handleFailedMutation);

const editDiscount = (editDiscountRequest: InventoryModel.Discount.EditRequest) =>
  discountApiV3
    .editDiscount(editDiscountRequest)
    .then((resp) => resp.data)
    .catch(handleFailedMutation);

const DISCOUNT_QUERY_KEY = 'discounts';
const DISCOUNT_DETAIL_QUERY_KEY = 'discount-detail';

const useDiscounts = (params: Omit<GetDiscountsParams, 'cursor'>) => {
  const { remove, ...rest } = useInfiniteQuery<InventoryModel.Discount.List200Response>(
    [DISCOUNT_QUERY_KEY, params.outletId],
    ({ pageParam }) =>
      listDiscounts({
        ...params,
        cursor: pageParam,
      }),
    {
      getNextPageParam: (_lastGroup) => _lastGroup?.next_cursor,
      refetchOnWindowFocus: false,
      refetchIntervalInBackground: false,
      refetchInterval: false,
    },
  );

  useEffect(() => {
    return () => {
      remove();
    };
  }, [remove]);

  return {
    ...rest,
  };
};

const mapPageResult = (page: InventoryModel.Discount.List200Response) => page.results;
const findSameName = (name: string, discount: InventoryModel.Discount.List200ResponseInner) => discount.name === name;
const mapOutletId = (outlet: InventoryModel.Discount.List200ResponseInnerOutlet) => outlet.outlet_id;
const getInitialData = (data: InfiniteData<InventoryModel.Discount.List200Response> | undefined, param: GetDiscountDetailParams) => {
  const initialData = (data?.pages ?? []).flatMap(mapPageResult).find(findSameName.bind({}, param.name));

  if (initialData) {
    return {
      ...initialData,
      outlet_ids: initialData.outlets.map(mapOutletId).sort(),
      business_id: -1,
    };
  }

  return undefined;
};

const useDiscountDetail = (param: GetDiscountDetailParams) => {
  return useQuery<InventoryModel.Discount.GetDetail200Response>(
    [DISCOUNT_DETAIL_QUERY_KEY, param.name],
    getDiscountDetail.bind(null, param),
    {
      initialData: getInitialData.bind(
        {},
        queryClient.getQueryData<InfiniteData<InventoryModel.Discount.List200Response>>([DISCOUNT_QUERY_KEY], {
          exact: false,
        }),
        param,
      ),
      refetchOnWindowFocus: false,
      refetchIntervalInBackground: false,
      refetchInterval: false,
    },
  );
};

const invalidateDiscounts = () => queryClient.invalidateQueries([DISCOUNT_QUERY_KEY]);

const invalidateDiscountDetail = (qC: QueryClient, discountName: string) => qC.invalidateQueries([DISCOUNT_DETAIL_QUERY_KEY, discountName]);

export type { MutationError };

export {
  DISCOUNTS_URL,
  DISCOUNT_V3_URL,
  DISCOUNT_V3_DETAIL_URL,
  createDiscount,
  editDiscount,
  deleteDiscount,
  useDiscounts,
  useDiscountDetail,
  invalidateDiscounts,
  invalidateDiscountDetail,
  handleFailedMutation as _handleFailedMutation,
  mapPageResult as _mapPageResult,
  findSameName as _findSameName,
  mapOutletId as _mapOutletId,
  getInitialData as _getInitialData,
};
